import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import type { Signal } from "@ct/shared-types";
import { Coin } from "../components/ui/Coin.js";
import { Icon } from "../components/ui/Icon.js";
import { fetchRecentSignals } from "../lib/api.js";
import { marketSocket } from "../lib/ws.js";

const FILTERS = ["All", "≥80% conf", "By strategy", "By symbol"] as const;
type Filter = (typeof FILTERS)[number];

function coinSym(symbol: string): string {
  return symbol.replace(/USDT$|USD$|USDC$/, "");
}

function ageOf(ms: number): string {
  const seconds = Math.max(0, (Date.now() - ms) / 1000);
  if (seconds < 60) return `${Math.floor(seconds)}s`;
  const minutes = seconds / 60;
  if (minutes < 60) return `${Math.floor(minutes)}m`;
  const hours = minutes / 60;
  return `${hours.toFixed(1)}h`;
}

function expiresIn(expiresAt: number): string {
  const ms = expiresAt - Date.now();
  if (ms <= 0) return "expired";
  const minutes = Math.floor(ms / 60_000);
  const hours = Math.floor(minutes / 60);
  const remMin = minutes - hours * 60;
  return hours > 0 ? `${hours}h ${remMin}m` : `${minutes}m`;
}

function fmt(n: number | undefined, digits = 2): string {
  if (n === undefined) return "—";
  return n.toLocaleString("en", { maximumFractionDigits: digits });
}

interface SignalCardProps {
  signal: Signal;
}

function SignalCard({ signal }: SignalCardProps) {
  const conf = Math.round(signal.confidence * 100);
  const score = Math.round(signal.compositeScore ?? signal.confidence * 100);
  const sym = coinSym(signal.symbol);
  return (
    <div className="se-ai-card" style={{ padding: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
        <Coin sym={sym} />
        <div>
          <div style={{ fontSize: 13, fontWeight: 600 }}>{signal.symbol}</div>
          <div className="muted" style={{ fontSize: 10 }}>
            {signal.exchange} · {signal.timeframe} timeframe
          </div>
        </div>
        <span className="se-pill se-pill-buy" style={{ marginLeft: 8 }}>BUY</span>
        {signal.marketRegime ? (
          <span className="se-pill se-pill-muted">Regime: {signal.marketRegime.replace(/_/g, " ")}</span>
        ) : null}
        <div
          style={{
            marginLeft: "auto",
            display: "flex",
            alignItems: "baseline",
            gap: 12,
          }}
        >
          <div style={{ textAlign: "right" }}>
            <div className="muted" style={{ fontSize: 10 }}>Score</div>
            <div
              className="mono tnum ai-text"
              style={{ fontSize: 16, fontWeight: 600, lineHeight: 1 }}
            >
              {score}
              <span className="muted" style={{ fontSize: 10, fontWeight: 400 }}>/100</span>
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <span className="muted" style={{ fontSize: 10, width: 70 }}>Confidence</span>
        <div className="se-progress" style={{ flex: 1 }}>
          <div className="se-progress-bar ai" style={{ width: `${conf}%` }} />
        </div>
        <span
          className="mono tnum ai-text"
          style={{ fontSize: 12, fontWeight: 600, width: 36, textAlign: "right" }}
        >
          {conf}%
        </span>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 8,
          fontSize: 11,
          marginBottom: 10,
        }}
      >
        <div>
          <div className="muted" style={{ fontSize: 10 }}>Entry</div>
          <div className="mono tnum">{fmt(signal.entryPrice)}</div>
        </div>
        <div>
          <div className="muted" style={{ fontSize: 10 }}>Stop-loss</div>
          <div className="mono tnum down">{fmt(signal.stopLoss)}</div>
        </div>
        <div>
          <div className="muted" style={{ fontSize: 10 }}>TP1 / TP2 / TP3</div>
          <div className="mono tnum up">
            {fmt(signal.takeProfit1)} / {fmt(signal.takeProfit2)} /{" "}
            {fmt(signal.takeProfit3)}
          </div>
        </div>
        <div>
          <div className="muted" style={{ fontSize: 10 }}>R / R</div>
          <div className="mono tnum">{signal.riskRewardRatio.toFixed(2)}</div>
        </div>
      </div>

      <div style={{ fontSize: 11 }}>
        <span className="muted" style={{ fontSize: 10 }}>Strategy </span>
        <span
          className="se-tag"
          style={{ borderColor: "rgba(168, 85, 247, 0.3)", color: "#C084FC" }}
        >
          {signal.strategyId}
        </span>
        {signal.indicatorsTriggered.length > 0 ? (
          <span style={{ marginLeft: 8, color: "var(--text-mid)" }}>
            · {signal.indicatorsTriggered.join(" · ")}
          </span>
        ) : null}
      </div>

      <div
        style={{
          display: "flex",
          gap: 8,
          marginTop: 12,
          paddingTop: 10,
          borderTop: "1px solid var(--border)",
          alignItems: "center",
        }}
      >
        <span className="muted" style={{ fontSize: 10 }}>
          {ageOf(signal.issuedAt)} ago · expires
        </span>
        <span className="mono tnum ai-text" style={{ fontSize: 11 }}>
          {expiresIn(signal.expiresAt)}
        </span>
        <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
          <button type="button" className="se-btn se-btn-sm">
            Paper
          </button>
          <button type="button" className="se-btn se-btn-ai se-btn-sm">
            Apply →
          </button>
        </div>
      </div>
    </div>
  );
}

export function AISignalsPage() {
  const [filter, setFilter] = useState<Filter>("All");
  const [liveSignals, setLiveSignals] = useState<Signal[]>([]);

  const initial = useQuery({
    queryKey: ["recent-signals"],
    queryFn: () => fetchRecentSignals(50),
    refetchInterval: 30_000,
  });

  useEffect(() => {
    if (initial.data) setLiveSignals(initial.data);
  }, [initial.data]);

  useEffect(
    () =>
      marketSocket.subscribe("signals:all", (_c, payload) => {
        const sig = payload as Signal;
        setLiveSignals((cur) => {
          if (cur.some((s) => s.id === sig.id)) return cur;
          return [sig, ...cur].slice(0, 100);
        });
      }),
    [],
  );

  const filtered = liveSignals.filter((s) => {
    if (filter === "≥80% conf") return s.confidence >= 0.8;
    return true;
  });

  const active = filtered.filter((s) => s.expiresAt > Date.now()).length;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div
        style={{
          padding: "12px 18px",
          display: "flex",
          alignItems: "center",
          gap: 12,
          borderBottom: "1px solid var(--border)",
        }}
      >
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, display: "flex", gap: 8 }}>
            AI Signals
            <span className="se-pill se-pill-ai">
              <Icon name="sparkle" size={10} /> Live · {active} active
            </span>
          </div>
          <div className="muted" style={{ fontSize: 11 }}>
            Real-time stream · model v0.1 · spot BUY entries only
          </div>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <div className="se-seg">
            {FILTERS.map((f) => (
              <button
                key={f}
                type="button"
                className={f === filter ? "active" : ""}
                onClick={() => setFilter(f)}
              >
                {f}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: 18 }}>
        {initial.isLoading ? (
          <div className="muted" style={{ fontSize: 12 }}>Loading signals…</div>
        ) : filtered.length === 0 ? (
          <div className="se-card" style={{ padding: 20, textAlign: "center" }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>No signals yet</div>
            <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
              The strategy engine will start emitting BUY entries as soon as
              indicators align.
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {filtered.map((s) => (
              <SignalCard key={s.id} signal={s} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
